import type { Draft } from 'immer';
import type { Body, CustomComponent, LayerRole, LevelDoc, PaletteEntry, PlacedObject, PropMod, TerrainShape, TileLayer, Vec2, Waypoint } from '../model/format.ts';
import { emptyMovementVars, flagsFromMatrix, matrixFromFlags } from '../model/format.ts';
import { terrainTiles } from '../model/bake.ts';
import { compiledStyle, terrainStyles } from '../terrain/index.ts';
import { outlineCells } from './outline.ts';
import { pointInRing } from './polygon.ts';
import { draftContainer, draftObject, getContainer, isLive, uid, useEditor, type Selection } from './store.ts';
import { REMOVE, cellsInRect, getCell, setCells } from './tileOps.ts';
import { layerTiles } from './scene.ts';
import { setModIn } from './components.ts';
import { placementPose } from './catalog.ts';

const SOLID_ROLES = new Set<LayerRole>(['level', 'oneway', 'spikes', 'foreground']);

export { setModIn };

const BODY_SORTING: Record<string, string> = {
  level: 'Moving Platform',
  foreground: 'Moving Platform FG',
  background: 'Moving Platform BG',
  spikes: 'Moving Platform',
  oneway: 'Moving Platform',
};
const LEVEL_SORTING: Record<string, string> = { level: 'Level', foreground: 'Foreground', background: 'Background', spikes: 'Level', oneway: 'Level' };

export function newLayer(role: LayerRole, inBody: boolean, name?: string): TileLayer {
  const sorting = (inBody ? BODY_SORTING : LEVEL_SORTING)[role] ?? 'Default';
  return {
    id: uid('L'),
    name: name ?? (role === 'level' ? 'Level' : role[0].toUpperCase() + role.slice(1)),
    role,
    sortingLayer: sorting,
    sortingOrder: role === 'spikes' ? 1 : 0,
    tiles: [],
    ...(role === 'level' || role === 'spikes' ? { spikeGenerator: true } : {}),
    ...(role === 'spikes' ? { collider: false } : {}),
  };
}

/** Find (or create) the container layer for a role; returns its id. */
export function ensureLayer(c: { layers: Draft<TileLayer>[] }, role: LayerRole, inBody: boolean): string {
  const l = c.layers.find((x) => x.role === role);
  if (l) return l.id;
  const nl = newLayer(role, inBody);
  c.layers.push(nl);
  return nl.id;
}

// ---------- tile flags ----------

export function rotateFlags(f: number, ccw = true): number {
  const [a, b, c, d] = matrixFromFlags(f);
  // R(90 ccw) = [0 -1; 1 0] applied after M
  const r = ccw ? [0, -1, 1, 0] : [0, 1, -1, 0];
  return flagsFromMatrix(r[0] * a + r[1] * c, r[0] * b + r[1] * d, r[2] * a + r[3] * c, r[2] * b + r[3] * d);
}

export function flipFlags(f: number, axis: 'x' | 'y'): number {
  const [a, b, c, d] = matrixFromFlags(f);
  return axis === 'x' ? flagsFromMatrix(-a, -b, c, d) : flagsFromMatrix(a, b, -c, -d);
}

// ---------- terrain ----------

/** A new object, so editing one polygon cannot change the others that started from the same style default. */
export function copyDecoration(src?: { enabled?: boolean; seed?: number; density?: number; foreground?: boolean; background?: boolean; edge?: TerrainShape['decoration']['edge']; kinds?: TerrainShape['decoration']['kinds'] }): TerrainShape['decoration'] {
  return {
    enabled: src?.enabled ?? true,
    seed: Math.floor(Math.random() * 1e6) || 1,
    density: src?.density ?? 1,
    foreground: src?.foreground ?? true,
    background: src?.background ?? true,
    ...(src?.edge ? { edge: src.edge } : {}),
    ...(src?.kinds ? { kinds: { ...src.kinds } } : {}),
  };
}

export function copyShading(src?: { enabled?: boolean; depth?: number; seed?: number; jitter?: number }): TerrainShape['shading'] {
  return { enabled: src?.enabled ?? true, depth: src?.depth ?? 2, seed: Math.floor(Math.random() * 1e6) || 1, jitter: src?.jitter ?? 0.5 };
}

export function createTerrain(containerId: string, ring: Vec2[], style: string, mode: 'add' | 'subtract'): string {
  const id = uid('t');
  const st = terrainStyles().find((s) => s.id === style);
  useEditor.getState().change(mode === 'add' ? 'Draw terrain' : 'Cut terrain', (d) => {
    const c = draftContainer(d, containerId);
    const inBody = containerId !== 'level';
    const t: TerrainShape = {
      id,
      style,
      mode,
      rings: [ring],
      shading: copyShading(st?.defaultShading),
      decoration: copyDecoration(st?.defaultDecoration),
      targets: {
        level: ensureLayer(c, 'level', inBody),
        foreground: ensureLayer(c, 'foreground', inBody),
        background: ensureLayer(c, 'background', inBody),
      },
    };
    if (mode === 'subtract') {
      const prev = [...c.terrains].reverse().find((x) => x.mode === 'add');
      if (prev) {
        t.style = prev.style;
        t.targets = { ...prev.targets };
      }
    }
    c.terrains.push(t);
  });
  return id;
}

/** Painted tiles in a cell, ignoring generated terrain and erase markers. */
function manualCellLayers(c: { layers: TileLayer[] }, x: number, y: number): TileLayer[] {
  const out: TileLayer[] = [];
  for (const l of c.layers) {
    if (l.locked || l.visible === false) continue;
    const cell = getCell(l.tiles as number[], x, y);
    if (cell && cell[0] >= 0) out.push(l);
  }
  return out;
}

/**
 * 4-connected painted tiles from (x, y). Ground, one-way, spikes and foreground
 * count as one blob. Clicking a decoration or background tile stays on that role.
 */
export function contiguousTileCells(doc: LevelDoc, containerId: string, x: number, y: number): [number, number][] {
  const c = getContainer(doc, containerId);
  const start = manualCellLayers(c, x, y);
  if (!start.length) return [];
  const startSolid = start.some((l) => SOLID_ROLES.has(l.role));
  const startRoles = new Set(start.map((l) => l.role));
  const occupy = (cx: number, cy: number) => {
    const ls = manualCellLayers(c, cx, cy);
    return startSolid ? ls.some((l) => SOLID_ROLES.has(l.role)) : ls.some((l) => startRoles.has(l.role));
  };
  const out: [number, number][] = [];
  const seen = new Set<string>();
  const q: [number, number][] = [[x, y]];
  seen.add(`${x},${y}`);
  while (q.length) {
    const [cx, cy] = q.pop()!;
    out.push([cx, cy]);
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as [number, number][]) {
      const nx = cx + dx, ny = cy + dy;
      const k = `${nx},${ny}`;
      if (seen.has(k) || !occupy(nx, ny)) continue;
      seen.add(k);
      q.push([nx, ny]);
    }
  }
  return out;
}

function entryKeys(e: PaletteEntry): string[] {
  const keys = [`${e.tex}|${e.sprite}`];
  if (e.tile) keys.push(`tile:${e.tile}`);
  return keys;
}

/** Sprites the current tileset uses as grass / edge art. */
function grassSpriteKeys(styleId: string): Set<string> {
  const keys = new Set<string>();
  const st = compiledStyle(styleId.split(':')[0]);
  if (!st) return keys;
  const add = (ch: number) => {
    if (ch < 0) return;
    const pe = st.sprites[ch >> 3];
    if (pe) for (const k of entryKeys(pe)) keys.add(k);
  };
  const m = st.data.fg.marginal;
  for (let i = 0; i < m.length; i += 2) add(m[i]);
  for (const d of st.rules.fg.values()) for (const c of d.c) add(c);
  for (const d of st.ends.values()) for (const c of d.c) add(c);
  for (const d of st.cases.grass.values()) for (const c of d.c) add(c);
  return keys;
}

function tileIsGrass(l: TileLayer, pal: number, doc: { palette: PaletteEntry[] }, grass: Set<string>): boolean {
  if (l.role === 'foreground') return true;
  const e = doc.palette[pal];
  return !!e && entryKeys(e).some((k) => grass.has(k));
}

function tileIsDirt(l: TileLayer, pal: number, doc: { palette: PaletteEntry[] }, grass: Set<string>): boolean {
  if (l.role !== 'level' && l.role !== 'oneway' && l.role !== 'spikes') return false;
  return !tileIsGrass(l, pal, doc, grass);
}

/** Click a tile: convert that connected blob into a terrain polygon. */
export function tilesToPolygonAt(x: number, y: number): string | null {
  const st = useEditor.getState();
  if (!st.doc) return null;
  const cells = contiguousTileCells(st.doc, st.container, Math.floor(x), Math.floor(y));
  if (!cells.length) {
    st.notify('No tiles there');
    return null;
  }
  return tilesToPolygonCells(cells);
}

/**
 * Turn painted tiles into terrain polygons: one shape per outline, with holes
 * where the tiles leave a gap. The tiles are removed so the new shape draws them.
 * All-grass blobs get Full grass; mixed blobs get None and the grass cells are painted on.
 */
export function tilesToPolygonCells(cells: [number, number][]): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  if (!doc) return null;
  const style = st.polygon.style || terrainStyles()[0]?.id || '';
  if (!style) {
    st.notify('Pick a terrain style first');
    return null;
  }
  if (!cells.length) {
    st.notify('No tiles in that area');
    return null;
  }
  const c = getContainer(doc, st.container);
  const loops = outlineCells(cells);
  const outers = loops.filter((ring) => ringArea(ring) > 0);
  const holes = loops.filter((ring) => ringArea(ring) < 0);
  if (!outers.length) {
    st.notify('Those tiles do not make a closed shape');
    return null;
  }
  const shaped = outers.map((outer) => ({
    outer,
    holes: holes.filter((hole) => hole.length > 0 && pointInRing(hole[0][0] + 0.01, hole[0][1] + 0.01, outer)),
  }));
  const grassKeys = grassSpriteKeys(style);
  const grassCells: [number, number][] = [];
  let dirt = 0;
  for (const [x, y] of cells) {
    let g = false, d = false;
    for (const l of manualCellLayers(c, x, y)) {
      const cell = getCell(l.tiles as number[], x, y);
      if (!cell) continue;
      if (tileIsGrass(l, cell[0], doc, grassKeys)) g = true;
      if (tileIsDirt(l, cell[0], doc, grassKeys)) d = true;
    }
    if (g) grassCells.push([x, y]);
    if (d) dirt++;
  }
  const allGrass = grassCells.length > 0 && dirt === 0;
  const gen = terrainTiles(doc, c);
  const preset = terrainStyles().find((s) => s.id === style);
  const ids: string[] = [];
  st.change(cells.length === 1 ? 'Convert tile to polygon' : `Convert ${cells.length} tiles to polygon`, (d) => {
    const dc = draftContainer(d, st.container);
    const inBody = st.container !== 'level';
    const decoration = copyDecoration(preset?.defaultDecoration);
    decoration.edge = allGrass ? 'full' : 'none';
    for (const shape of shaped) {
      const id = uid('t');
      ids.push(id);
      const t: TerrainShape = {
        id,
        style,
        mode: 'add',
        rings: [shape.outer, ...shape.holes],
        shading: copyShading(preset?.defaultShading),
        decoration: { ...decoration, seed: Math.floor(Math.random() * 1e6) || 1 },
        targets: {
          level: ensureLayer(dc, 'level', inBody),
          foreground: ensureLayer(dc, 'foreground', inBody),
          background: ensureLayer(dc, 'background', inBody),
        },
      };
      dc.terrains.push(t);
    }
    for (const l of dc.layers) {
      if (l.locked) continue;
      const g = gen.get(l.id) ?? [];
      const genSet = new Set<string>();
      for (let i = 0; i < g.length; i += 4) if (g[i + 2] >= 0) genSet.add(`${g[i]},${g[i + 1]}`);
      const edits: { x: number; y: number; p: number; f: number }[] = [];
      for (const [x, y] of cells) {
        const manual = getCell(l.tiles as number[], x, y);
        const hasManual = !!manual && manual[0] >= 0;
        const generated = genSet.has(`${x},${y}`);
        if (!hasManual && !generated) continue;
        edits.push({ x, y, p: generated ? -1 : REMOVE, f: 0 });
      }
      if (edits.length) l.tiles = setCells(l.tiles as number[], edits);
    }
    if (!allGrass && grassCells.length) {
      const m = new Map<string, [number, number, number]>();
      if (dc.grassPaint) for (let i = 0; i + 2 < dc.grassPaint.length; i += 3) m.set(`${dc.grassPaint[i]},${dc.grassPaint[i + 1]}`, [dc.grassPaint[i], dc.grassPaint[i + 1], dc.grassPaint[i + 2]]);
      for (const [x, y] of grassCells) m.set(`${x},${y}`, [x, y, 1]);
      dc.grassPaint = [...m.values()].flat();
    }
  });
  if (ids[0]) st.set({ selection: { kind: 'terrain', id: ids[0] } });
  return ids[0] ?? null;
}

/** @deprecated click the Tiles to polygon tool instead; kept for the old rectangle path */
export function tilesToPolygon(rect: [number, number, number, number]): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  if (!doc) return null;
  const [x0, y0, x1, y1] = rect;
  const c = getContainer(doc, st.container);
  const cells: [number, number][] = [];
  const seen = new Set<string>();
  for (const l of c.layers) {
    if (l.locked || l.visible === false) continue;
    const tiles = layerTiles(doc, c, l);
    for (let i = 0; i < tiles.length; i += 4) {
      const x = tiles[i], y = tiles[i + 1], p = tiles[i + 2];
      if (p < 0 || x < x0 || x > x1 || y < y0 || y > y1) continue;
      const k = `${x},${y}`;
      if (seen.has(k)) continue;
      seen.add(k);
      cells.push([x, y]);
    }
  }
  return tilesToPolygonCells(cells);
}

function ringArea(ring: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

export function addHole(containerId: string, terrainId: string, ring: Vec2[]) {
  useEditor.getState().change('Add hole', (d) => {
    const t = draftContainer(d, containerId).terrains.find((x) => x.id === terrainId);
    if (t) t.rings.push(ring);
  });
}

export function findTerrain(doc: LevelDoc, id: string): { terrain: TerrainShape; container: string } | null {
  const t = doc.terrains.find((x) => x.id === id);
  if (t) return { terrain: t, container: 'level' };
  for (const b of doc.bodies) {
    const bt = b.terrains.find((x) => x.id === id);
    if (bt) return { terrain: bt, container: b.id };
  }
  return null;
}

// ---------- objects ----------

export function addObject(prefab: string, name: string, x: number, y: number, bodyId?: string): string {
  const id = uid('o');
  useEditor.getState().change(`Add ${name}`, (d) => {
    const pose = placementPose(prefab);
    const o: PlacedObject = { id, name, prefab, x, y, rotation: pose.rotation, scale: pose.scale, mods: [] };
    const b = bodyId ? d.bodies.find((x) => x.id === bodyId) : null;
    (b ? b.objects : d.objects).push(o);
  });
  return id;
}

const OBJECT_FILL_MAX = 2500;

function objectList(d: Draft<LevelDoc>, bodyId?: string): Draft<PlacedObject>[] | null {
  if (!bodyId) return d.objects;
  return d.bodies.find((b) => b.id === bodyId)?.objects ?? null;
}

/** One object per 1×1 grid square, at the cell centre. Returns the new id, or null if that cell already has this prefab. */
export function stampObject(d: Draft<LevelDoc>, prefab: string, name: string, cellX: number, cellY: number, bodyId?: string): string | null {
  const list = objectList(d, bodyId);
  if (!list) return null;
  if (list.some((o) => o.prefab === prefab && Math.floor(o.x) === cellX && Math.floor(o.y) === cellY)) return null;
  const id = uid('o');
  const pose = placementPose(prefab);
  list.push({ id, name, prefab, x: cellX + 0.5, y: cellY + 0.5, rotation: pose.rotation, scale: pose.scale, mods: [] });
  return id;
}

/** Place into a grid cell during a live drag, or as its own undo step. */
export function placeObjectCell(cellX: number, cellY: number, prefab: string, name: string, bodyId?: string): string | null {
  const s = useEditor.getState();
  let id: string | null = null;
  const recipe = (d: Draft<LevelDoc>) => {
    id = stampObject(d, prefab, name, cellX, cellY, bodyId);
  };
  if (isLive()) s.live(recipe);
  else s.change(`Add ${name}`, recipe);
  return id;
}

export function fillObjectCells(cells: [number, number][], prefab: string, name: string, bodyId?: string): number {
  if (cells.length > OBJECT_FILL_MAX) {
    useEditor.getState().notify('Area too large');
    return 0;
  }
  let n = 0;
  useEditor.getState().change(`Fill ${name}`, (d) => {
    for (const [x, y] of cells) if (stampObject(d, prefab, name, x, y, bodyId)) n++;
  });
  return n;
}

export function setMod(d: Draft<LevelDoc>, objId: string, t: string, p: string, value: { v?: string; r?: string } | null) {
  const o = draftObject(d, objId);
  if (!o) return;
  setModIn(o.mods, t, p, value);
}

/** Refs that point at an entity (for cleanup when deleting). */
export function stripRefs(d: Draft<LevelDoc>, refPrefix: string) {
  const keep = (r?: string) => !r || !(r === refPrefix || r.startsWith(refPrefix + '#'));
  const clean = (mods: Draft<PropMod>[]) => {
    for (let i = mods.length - 1; i >= 0; i--) if (!keep(mods[i].r)) mods.splice(i, 1);
  };
  const isDead = (v: any) => v && typeof v === 'object' && !keep(v.ref ?? v.entity);
  const cleanData = (v: any): void => {
    if (Array.isArray(v)) {
      for (let i = v.length - 1; i >= 0; i--) {
        const x = v[i];
        if (isDead(x) || (x && typeof x === 'object' && Object.values(x).some(isDead))) v.splice(i, 1);
        else cleanData(x);
      }
    } else if (v && typeof v === 'object') {
      for (const k of Object.keys(v)) {
        if (isDead(v[k])) v[k] = { fileID: '0' };
        else cleanData(v[k]);
      }
    }
  };
  const cleanComps = (list?: Draft<CustomComponent>[]) => list?.forEach((c) => cleanData(c.data));
  const cleanObj = (o: Draft<PlacedObject>) => {
    clean(o.mods);
    cleanComps(o.components);
  };
  d.objects.forEach(cleanObj);
  for (const b of d.bodies) {
    clean(b.mods);
    cleanComps(b.components);
    b.layers.forEach((l) => cleanComps(l.extraComponents));
    b.objects.forEach(cleanObj);
    if (b.moving) b.moving.activators = b.moving.activators.filter((a) => keep(a));
  }
}

export function deleteSelection(sel: Selection) {
  const st = useEditor.getState();
  if (sel.kind === 'objects') {
    st.change(sel.ids.length > 1 ? `Delete ${sel.ids.length} objects` : 'Delete object', (d) => {
      const ids = new Set(sel.ids);
      d.objects = d.objects.filter((o) => !ids.has(o.id) && !(o.attach && ids.has(o.attach.object)));
      for (const b of d.bodies) b.objects = b.objects.filter((o) => !ids.has(o.id));
      d.bodies = d.bodies.filter((b) => !(b.owner && ids.has(b.owner.object)));
      sel.ids.forEach((id) => stripRefs(d, `obj:${id}`));
    });
  } else if (sel.kind === 'body') {
    st.change('Delete body', (d) => {
      d.bodies = d.bodies.filter((b) => b.id !== sel.id);
      stripRefs(d, `body:${sel.id}`);
    });
    if (st.container === sel.id) st.set({ container: 'level' });
  } else if (sel.kind === 'terrain') {
    st.change('Delete terrain', (d) => {
      d.terrains = d.terrains.filter((t) => t.id !== sel.id);
      for (const b of d.bodies) b.terrains = b.terrains.filter((t) => t.id !== sel.id);
    });
  } else if (sel.kind === 'waypoint') {
    st.change('Delete waypoint', (d) => {
      const b = d.bodies.find((x) => x.id === sel.body);
      if (b?.moving) b.moving.waypoints = b.moving.waypoints.filter((w) => w.id !== sel.id);
    });
  } else if (sel.kind === 'cells') {
    const [x0, y0, x1, y1] = sel.rect;
    st.change('Clear cells', (d) => {
      const doc = st.doc!;
      const c = getContainer(doc, st.container);
      const dc = draftContainer(d, st.container);
      const gen = terrainTiles(doc, c);
      for (const l of dc.layers) {
        if (l.locked) continue;
        const cells: { x: number; y: number; p: number; f: number }[] = [];
        const g = gen.get(l.id) ?? [];
        const genSet = new Set<string>();
        for (let i = 0; i < g.length; i += 4) genSet.add(`${g[i]},${g[i + 1]}`);
        for (let x = x0; x <= x1; x++) for (let y = y0; y <= y1; y++) cells.push({ x, y, p: genSet.has(`${x},${y}`) ? -1 : REMOVE, f: 0 });
        l.tiles = setCells(l.tiles as number[], cells);
      }
    });
  }
  st.set({ selection: { kind: 'none' } });
}

export function duplicateObjects(ids: string[]): string[] {
  const st = useEditor.getState();
  const out: string[] = [];
  st.change('Duplicate', (d) => {
    for (const id of ids) {
      const o = draftObject(d, id);
      if (!o) continue;
      const copy = JSON.parse(JSON.stringify(o)) as PlacedObject;
      copy.id = uid('o');
      copy.name = o.name;
      copy.x += 1;
      out.push(copy.id);
      const inBody = d.bodies.find((b) => b.objects.some((x) => x.id === id));
      (inBody ? inBody.objects : d.objects).push(copy);
    }
  });
  return out;
}

// ---------- bodies ----------

export function defaultMoving(origin: Vec2, to: Vec2): Body['moving'] {
  return {
    activators: [],
    activationType: 0,
    defaultMovementVars: { ...emptyMovementVars(), speedEnum: 3, accEnum: 2 },
    deactivationAction: 0,
    finishAction: 2,
    dontUseStartAsMovementPoint: false,
    start: { movementVars: emptyMovementVars(), pauseOnArrivalForSeconds: 0, pausePlatformOnArrival: false, warpToNextPoint: false },
    waypoints: [newWaypoint(to[0], to[1], 0)],
  };
}

export function newWaypoint(x: number, y: number, index: number): Waypoint {
  return {
    id: uid('w'),
    name: index ? `MoveTo (${index})` : 'MoveTo',
    x, y,
    movementVars: emptyMovementVars(),
    pauseOnArrivalForSeconds: 0,
    pausePlatformOnArrival: false,
    warpToNextPoint: false,
  };
}

/**
 * Detach the cells in a rect of the level (all unlocked layers, generated terrain baked in) into a new body.
 * Source cells are removed (manual) or masked (generated terrain) so the level keeps its other tiles.
 */
export function detachToBody(rect: [number, number, number, number], behavior: Body['behavior'] = 'movingPlatform'): string | null {
  const st = useEditor.getState();
  const doc = st.doc;
  if (!doc) return null;
  const [x0, y0, x1, y1] = rect;
  const c = getContainer(doc, 'level');
  const gen = terrainTiles(doc, c);
  const grabbed: { layer: TileLayer; cells: number[] }[] = [];
  for (const l of c.layers) {
    if (l.locked || l.visible === false) continue;
    const cells = cellsInRect(layerTiles(doc, c, l), x0, y0, x1, y1);
    if (cells.length) grabbed.push({ layer: l, cells });
  }
  const objs = [...doc.objects].filter((o) => !o.attach && o.x >= x0 && o.x <= x1 + 1 && o.y >= y0 && o.y <= y1 + 1 && !doc.bodies.some((b) => b.owner?.object === o.id));
  if (!grabbed.length) {
    st.notify('Nothing to detach in that area');
    return null;
  }
  const id = uid('b');
  st.change('Detach to body', (d) => {
    const origin: Vec2 = [x0, y0];
    const body: Body = {
      id,
      name: behavior === 'movingPlatform' ? 'Moving Platform' : 'Body',
      behavior,
      placement: 'behindLevel',
      origin,
      layers: [],
      terrains: [],
      objects: [],
      mods: [],
      moving: behavior === 'movingPlatform' ? defaultMoving(origin, [x0, y1 + 2]) : undefined,
    };
    for (const g of grabbed) {
      const role = g.layer.role;
      const local: number[] = [];
      for (let i = 0; i < g.cells.length; i += 4) local.push(g.cells[i] - x0, g.cells[i + 1] - y0, g.cells[i + 2], g.cells[i + 3]);
      body.layers.push({
        ...newLayer(role, true, g.layer.name),
        color: g.layer.color,
        spikeGenerator: g.layer.spikeGenerator,
        oneWayGenerator: g.layer.oneWayGenerator,
        tiles: local,
      });
      const src = d.layers.find((l) => l.id === g.layer.id)!;
      const genL = gen.get(g.layer.id) ?? [];
      const genSet = new Set<string>();
      for (let i = 0; i < genL.length; i += 4) genSet.add(`${genL[i]},${genL[i + 1]}`);
      const edits: { x: number; y: number; p: number; f: number }[] = [];
      for (let i = 0; i < g.cells.length; i += 4) {
        const x = g.cells[i], y = g.cells[i + 1];
        edits.push({ x, y, p: genSet.has(`${x},${y}`) ? -1 : REMOVE, f: 0 });
      }
      src.tiles = setCells(src.tiles as number[], edits);
    }
    const objIds = new Set(objs.map((o) => o.id));
    body.objects = d.objects.filter((o) => objIds.has(o.id)) as PlacedObject[];
    d.objects = d.objects.filter((o) => !objIds.has(o.id));
    d.bodies.push(body);
  });
  st.set({ selection: { kind: 'body', id } });
  return id;
}

/** Move a body (content origin) and its waypoints/riders by a delta. */
export function moveBody(d: Draft<LevelDoc>, bodyId: string, dx: number, dy: number, withWaypoints = true) {
  const b = d.bodies.find((x) => x.id === bodyId);
  if (!b) return;
  b.origin = [b.origin[0] + dx, b.origin[1] + dy];
  if (b.handlerPosition) b.handlerPosition = [b.handlerPosition[0] + dx, b.handlerPosition[1] + dy];
  if (withWaypoints && b.moving) for (const w of b.moving.waypoints) { w.x += dx; w.y += dy; }
  for (const o of b.objects) { o.x += dx; o.y += dy; }
}
